import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Check, X, ChevronRight, GitCompare, Home } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { COMPETITORS, FAQ_COMPARE } from '../mock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

function Cell({ v }) {
  if (v === true) return <span className="inline-flex items-center gap-1.5 text-[#7dd4c5]"><Check className="w-4 h-4" /> Yes</span>;
  if (v === false) return <span className="inline-flex items-center gap-1.5 text-[#7a7770]"><X className="w-4 h-4" /> No</span>;
  return <span className="text-[#e8e6e1]">{v}</span>;
}

export default function ComparisonDetail() {
  const { slug } = useParams();
  const c = COMPETITORS.find((x) => x.slug === slug);

  useEffect(() => {
    if (!c) return;
    document.title = `CrewFace vs ${c.name}: features, pricing and which is better in 2026`;
    const meta = document.querySelector('meta[name="description"]') || (() => { const m = document.createElement('meta'); m.name = 'description'; document.head.appendChild(m); return m; })();
    meta.content = `Honest comparison of CrewFace and ${c.name}. See features, pricing, turnaround, and use cases side by side. Best AI headshot tool for field crews in 2026.`;

    // Structured data (FAQPage + Product + BreadcrumbList)
    const existing = document.getElementById('crewface-jsonld');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'crewface-jsonld';
    script.text = JSON.stringify([
      {
        '@context': 'https://schema.org', '@type': 'Product',
        name: 'CrewFace', description: 'AI headshot generator for remote blue-collar teams', brand: 'CrewFace',
        offers: { '@type': 'AggregateOffer', lowPrice: '49', highPrice: '199', priceCurrency: 'USD' }
      },
      {
        '@context': 'https://schema.org', '@type': 'FAQPage',
        mainEntity: FAQ_COMPARE(c.name).map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } }))
      },
      {
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: '/' },
          { '@type': 'ListItem', position: 2, name: 'Compare', item: '/compare' },
          { '@type': 'ListItem', position: 3, name: `CrewFace vs ${c.name}`, item: `/compare/${c.slug}` }
        ]
      }
    ]);
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [c]);

  if (!c) {
    return (
      <div className="min-h-screen bg-[#0a0a0c]">
        <Navbar />
        <div className="max-w-md mx-auto text-center py-32 px-5">
          <h2 className="serif text-3xl text-white mb-3">Comparison not found</h2>
          <Link to="/compare"><Button className="bg-[#ea9a6f] text-[#0a0a0c] rounded-full">View all comparisons</Button></Link>
        </div>
        <Footer />
      </div>
    );
  }

  const faqs = FAQ_COMPARE(c.name);

  return (
    <div className="min-h-screen bg-[#0a0a0c]">
      <Navbar />

      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-8 md:pt-10 pb-4">
        <div className="flex items-center gap-1.5 text-xs text-[#7a7770] mb-6">
          <Link to="/" className="hover:text-white flex items-center gap-1"><Home className="w-3.5 h-3.5" /> Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/compare" className="hover:text-white">Compare</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-white">CrewFace vs {c.name}</span>
        </div>

        <Badge className="bg-[#14141a] border border-[#26262e] text-[#c9c6bf] hover:bg-[#14141a] rounded-full mb-4">Comparison · 2026</Badge>
        <h1 className="serif text-4xl md:text-6xl text-white leading-[1.03] max-w-4xl">CrewFace vs {c.name}:<br />features, pricing and which is better in 2026</h1>
        <p className="text-[#a8a49b] mt-5 max-w-2xl text-lg">{c.tagline}. Here’s an honest side-by-side of where each tool wins.</p>

        <div className="flex flex-wrap items-center gap-3 mt-8">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#ea9a6f] flex items-center justify-center serif text-lg text-[#0a0a0c] font-semibold">CF</div>
            <div>
              <div className="text-white font-semibold">CrewFace</div>
              <div className="text-xs text-[#7a7770]">B2B · field crew headshots</div>
            </div>
          </div>
          <GitCompare className="w-5 h-5 text-[#5a5850] mx-2" />
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#1a1a20] border border-[#26262e] flex items-center justify-center serif text-lg text-[#c9c6bf]">{c.logoInitial}</div>
            <div>
              <div className="text-white font-semibold">{c.name}</div>
              <div className="text-xs text-[#7a7770]">{c.positioning}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Verdict card */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <div className="bg-[#101014] border border-[#1e1e26] rounded-2xl p-6 md:p-8">
          <div className="mono text-xs text-[#ea9a6f] uppercase tracking-wider mb-3">Verdict</div>
          <p className="text-[#e8e6e1] text-lg md:text-xl leading-relaxed max-w-3xl">{c.verdict}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/sign-up"><Button className="bg-[#ea9a6f] hover:bg-[#e0895a] text-[#0a0a0c] rounded-full h-11 px-6">Try CrewFace free <ArrowRight className="ml-1.5 w-4 h-4" /></Button></Link>
            <Link to="/compare"><Button variant="outline" className="border-[#2a2a30] bg-transparent text-white hover:bg-[#14141a] rounded-full h-11 px-6">All comparisons</Button></Link>
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-6">
        <h2 className="serif text-3xl md:text-4xl text-white mb-6">Side-by-side comparison</h2>
        <div className="bg-[#101014] border border-[#1e1e26] rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 gap-4 px-5 md:px-6 py-4 border-b border-[#1e1e26] text-xs uppercase tracking-wider text-[#7a7770]">
            <div className="col-span-6 md:col-span-5">Feature</div>
            <div className="col-span-3 md:col-span-3">CrewFace</div>
            <div className="col-span-3 md:col-span-4">{c.name}</div>
          </div>
          {Object.entries(c.features).map(([k, v], i) => (
            <div key={k} className={`grid grid-cols-12 gap-4 px-5 md:px-6 py-4 items-center text-sm ${i % 2 === 1 ? 'bg-[#0d0d11]' : ''}`}>
              <div className="col-span-6 md:col-span-5 text-[#c9c6bf]">{k}</div>
              <div className="col-span-3 md:col-span-3"><Cell v={v.us} /></div>
              <div className="col-span-3 md:col-span-4"><Cell v={v.them} /></div>
            </div>
          ))}
        </div>
      </section>

      {/* Pros & Cons */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-10 grid md:grid-cols-2 gap-4">
        <div className="bg-[#101014] border border-[#1e1e26] rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-[#ea9a6f] flex items-center justify-center serif text-[#0a0a0c] font-semibold">CF</div>
            <h3 className="text-white font-semibold">Where CrewFace wins</h3>
          </div>
          <ul className="space-y-2.5 text-sm text-[#c9c6bf]">
            {['Batch onboarding for 25–25,000 field workers', 'Admin dashboard, approval workflow, HRIS sync', 'ID badge exports (2x2, 35x45mm, PDF)', 'Uniform / PPE preservation on hi-vis and hard hats', 'SOC 2, SSO, SCIM, configurable selfie retention'].map((t) => (
              <li key={t} className="flex gap-2"><Check className="w-4 h-4 text-[#7dd4c5] shrink-0 mt-0.5" />{t}</li>
            ))}
          </ul>
        </div>
        <div className="bg-[#101014] border border-[#1e1e26] rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-[#1a1a20] border border-[#26262e] flex items-center justify-center serif text-[#c9c6bf]">{c.logoInitial}</div>
            <h3 className="text-white font-semibold">Where {c.name} wins</h3>
          </div>
          <ul className="space-y-2.5 text-sm text-[#c9c6bf]">
            {['Purpose-built for individual, consumer-style portraits', 'Wider range of artistic styles and outfits', 'One-time consumer pricing (no seat licenses)', 'Well-known consumer brand & LinkedIn positioning'].map((t) => (
              <li key={t} className="flex gap-2"><Check className="w-4 h-4 text-[#c9c6bf] shrink-0 mt-0.5" />{t}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-5 md:px-8 py-14">
        <h2 className="serif text-3xl md:text-4xl text-white mb-8 text-center">People also ask</h2>
        <Accordion type="single" collapsible className="space-y-2">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`i${i}`} className="bg-[#101014] border border-[#1e1e26] rounded-xl px-5">
              <AccordionTrigger className="text-white text-left hover:no-underline py-4">{f.q}</AccordionTrigger>
              <AccordionContent className="text-[#a8a49b] pb-4">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Related */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <h3 className="serif text-2xl text-white mb-5">Other CrewFace comparisons</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {COMPETITORS.filter((x) => x.slug !== c.slug).slice(0, 4).map((r) => (
            <Link key={r.slug} to={`/compare/${r.slug}`} className="bg-[#101014] border border-[#1e1e26] rounded-xl p-4 hover:border-[#ea9a6f]/50 transition-colors">
              <div className="text-[#7a7770] text-xs uppercase tracking-wider mb-1">Compare</div>
              <div className="text-white font-medium">CrewFace vs {r.name}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16">
        <div className="bg-gradient-to-br from-[#1a1611] to-[#100e0b] border border-[#2a2418] rounded-3xl p-10 md:p-14 text-center">
          <h2 className="serif text-4xl md:text-5xl text-white leading-tight max-w-2xl mx-auto">The best way to compare is to try.</h2>
          <p className="text-[#a8a49b] mt-3">2 free headshots. No card. Full admin dashboard.</p>
          <Link to="/sign-up">
            <Button className="bg-[#ea9a6f] hover:bg-[#e0895a] text-[#0a0a0c] rounded-full h-12 px-7 mt-6 glow-coral">Start with CrewFace <ArrowRight className="ml-1.5 w-4 h-4" /></Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
